import type { ExecutionCargoHydration } from '../../executionOperations/types';

export type HumanizedCargoMeta = {
  cargoId: number;
  name: string;
  quantity: number | null;
  weightKg: number | null;
  volumeM3: number | null;
  packagingType: string | null;
  applicationNumber: string | null;
  clientName: string | null;
};

export type EnrichedExecutionCargo = ExecutionCargoHydration & {
  human: HumanizedCargoMeta;
};

function toNum(value: unknown): number | null {
  if (value == null) return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

function toStr(value: unknown): string | null {
  if (value == null) return null;
  const s = String(value).trim();
  return s || null;
}

/** Join route view cargo / applications into execution cargo rows. */
export function buildCargoHumanizationIndex(
  routeCargo: Record<string, unknown>[] | undefined,
  applications: Array<{ application?: Record<string, unknown> }> | undefined
): Map<number, HumanizedCargoMeta> {
  const map = new Map<number, HumanizedCargoMeta>();
  const appById = new Map<number, Record<string, unknown>>();

  for (const entry of applications ?? []) {
    const app = (entry.application ?? entry) as Record<string, unknown>;
    if (!app?.id) continue;
    appById.set(Number(app.id), app);
  }

  for (const raw of routeCargo ?? []) {
    const cargoId = toNum(raw.id);
    if (cargoId == null) continue;

    const applicationId = toNum(raw.applicationId);
    const app = applicationId != null ? appById.get(applicationId) : undefined;
    const client = app?.client as Record<string, unknown> | undefined;

    const weightPerItem = toNum(raw.weightPerItem);
    const qty = toNum(raw.quantity);
    const totalWeight = toNum(raw.totalWeightKg);

    map.set(cargoId, {
      cargoId,
      name: toStr(raw.name) ?? `Груз #${cargoId}`,
      quantity: qty,
      weightKg: totalWeight ?? (weightPerItem != null && qty != null ? weightPerItem * qty : null),
      volumeM3: toNum(raw.volumeM3),
      packagingType: toStr(raw.packagingType),
      applicationNumber:
        toStr(app?.externalNumber) ?? toStr(app?.internalNumber) ?? (applicationId != null ? `#${applicationId}` : null),
      clientName: toStr(client?.name)
    });
  }

  return map;
}

export function enrichExecutionCargo(
  cargo: ExecutionCargoHydration[],
  index: Map<number, HumanizedCargoMeta>
): EnrichedExecutionCargo[] {
  return cargo.map((row) => ({
    ...row,
    human: index.get(row.cargoId) ?? {
      cargoId: row.cargoId,
      name: `Груз #${row.cargoId}`,
      quantity: row.plannedQty,
      weightKg: null,
      volumeM3: null,
      packagingType: null,
      applicationNumber: null,
      clientName: null
    }
  }));
}

export function formatCargoSubtitle(meta: HumanizedCargoMeta, plannedQty: number): string {
  const parts: string[] = [];
  parts.push(`${plannedQty} шт`);
  if (meta.weightKg != null) parts.push(`${Math.round(meta.weightKg)} кг`);
  if (meta.volumeM3 != null) parts.push(`${meta.volumeM3} м³`);
  if (meta.packagingType) parts.push(meta.packagingType);
  return parts.join(' • ');
}
